import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { reviews, stats } from '@/mocks/reviews';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d96f40cut53jbls7knc0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'website_alt')
            .map(([k, v]) => [k, String(v)])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        if (serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormError('提交内容被系统拦截，请检查后重试');
        } else {
          setFormStatus('error');
          setFormError(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  const faqs = [
    { q: '韩漫大全的漫画资源来自哪里？', a: '我们与多家正版韩国漫画平台合作，确保为用户提供高质量的漫画资源。我们致力于推广优质韩漫，让更多中文读者能够便捷地欣赏到精彩的韩国漫画作品。' },
    { q: '是否完全免费阅读？', a: '是的，韩漫大全目前所有漫画均免费提供给读者阅读。我们通过广告和会员服务维持运营，但核心阅读功能始终对用户免费开放。' },
    { q: '漫画更新频率如何？', a: '我们与韩国漫画平台保持同步更新，热门连载漫画通常在官方更新后的24小时内完成翻译和上线。部分热门作品甚至可以在数小时内更新。' },
    { q: '如何搜索我想看的漫画？', a: '您可以通过顶部导航栏的分类浏览找到感兴趣的漫画类型，也可以使用搜索框直接搜索漫画名称。我们还提供了热门推荐和最新更新等入口。' },
    { q: '可以在手机上阅读吗？', a: '当然可以！我们的网站采用响应式设计，完美适配手机、平板和电脑等各种设备。无论在通勤路上还是家中，都能随时享受韩漫阅读的乐趣。' },
  ];

  return (
    <>
      <Navbar />
      <main className="min-h-screen">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* Brand Introduction */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="flex flex-col items-center text-center mb-12">
              <div className="w-16 h-16 rounded-2xl bg-primary-100 flex items-center justify-center mb-4">
                <i className="ri-book-open-line text-primary-600 text-2xl"></i>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground-950">
                关于韩漫大全
              </h1>
              <p className="text-sm text-foreground-500 mt-3 max-w-xl">
                致力于打造中文世界最优质的韩国漫画在线阅读平台
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">我们的故事</h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  韩漫大全成立于2023年，由一群热爱韩国漫画的年轻人创办。我们发现，尽管韩国漫画在全球范围内越来越受欢迎，但中文读者获取优质韩漫资源的渠道仍然有限。许多优秀作品因为语言障碍而无法被中文读者欣赏。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  于是我们决定打造一个专注于韩国漫画的在线阅读平台。从最初的几十部漫画到如今上万部作品，从零星的日活跃用户到如今数百万读者——我们一路走来，始终秉持着"让每个人都能畅享优质韩漫"的初心。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  目前，韩漫大全已经成为中文互联网上最大的韩国漫画聚合平台之一，涵盖热血战斗、恋爱校园、奇幻冒险、恋爱宫廷、武侠仙侠等数十个分类，每日更新数百个漫画章节。
                </p>
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">核心优势</h2>
                <div className="space-y-4">
                  {[
                    { icon: 'ri-shield-check-line', title: '正版授权', desc: '与多家韩国漫画平台建立合作关系，确保内容的合法性与质量' },
                    { icon: 'ri-flashlight-line', title: '极速更新', desc: '同步韩国官方更新，热门作品24小时内完成翻译上线' },
                    { icon: 'ri-smartphone-line', title: '全端适配', desc: '完美适配手机、平板、电脑，随时随地畅享阅读体验' },
                    { icon: 'ri-translate', title: '专业翻译', desc: '拥有专业翻译团队，确保译文准确流畅、原汁原味' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex gap-4 p-4 rounded-lg bg-background-100 border border-background-200/60">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                        <i className={`${item.icon} text-primary-600`}></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                        <p className="text-xs text-foreground-500 mt-1">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full py-14 bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="flex flex-col items-center text-center p-6 rounded-lg bg-background-50 border border-background-200/60">
                  <span className="font-heading text-3xl md:text-4xl font-black text-primary-500">{stat.value}</span>
                  <span className="text-sm text-foreground-500 mt-2">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 text-center mb-10">
              读者好评
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {reviews.slice(0, 3).map((review) => (
                <article
                  key={review.id}
                  className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/70"
                >
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4 line-clamp-4">
                    {review.content}
                  </p>
                  <div className="flex items-center gap-3 pt-3 border-t border-background-200/50">
                    <div className="w-9 h-9 rounded-full overflow-hidden bg-background-200 shrink-0">
                      <img src={review.avatar} alt={review.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                      <p className="text-xs text-foreground-400">{review.role}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Business Cooperation */}
        <section id="cooperation" className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 text-center mb-10">
              商务合作
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-primary-100 flex items-center justify-center mb-4">
                  <i className="ri-team-line text-primary-600 text-xl"></i>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">内容合作</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  欢迎韩国漫画平台、翻译组、个人创作者与我们联系，共同推广优质韩漫内容。我们提供流量扶持和推广资源。
                </p>
                <a href="#contact" className="inline-block mt-4 text-sm text-primary-500 font-medium hover:text-primary-600">
                  联系合作 →
                </a>
              </div>
              <div className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-4">
                  <i className="ri-advertisement-line text-accent-600 text-xl"></i>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">广告投放</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  精准触达百万韩漫爱好者，提供Banner广告、信息流广告、品牌专区等多种广告形式，帮助您的品牌高效获客。
                </p>
                <a href="#contact" className="inline-block mt-4 text-sm text-primary-500 font-medium hover:text-primary-600">
                  了解详情 →
                </a>
              </div>
              <div className="p-6 rounded-lg bg-background-50 border border-background-200/70 text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-secondary-100 flex items-center justify-center mb-4">
                  <i className="ri-building-2-line text-secondary-600 text-xl"></i>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">企业服务</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  为企业客户提供定制化的漫画内容解决方案，包括IP授权、原创漫画定制、品牌漫画营销等专业服务。
                </p>
                <a href="#contact" className="inline-block mt-4 text-sm text-primary-500 font-medium hover:text-primary-600">
                  立即咨询 →
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 text-center mb-10">
              常见问题
            </h2>
            <div className="space-y-3">
              {faqs.map((faq, idx) => (
                <details key={idx} className="group rounded-lg bg-background-50 border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-900 pr-4">{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">联系我们</h2>
              <p className="text-sm text-foreground-500 mt-3">
                有任何问题或建议？欢迎随时与我们联系
              </p>
            </div>

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5 p-6 md:p-8 rounded-lg bg-background-100 border border-background-200/70"
            >
              {/* Honeypot */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-2">姓名</label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-2">邮箱</label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-2">主题</label>
                <select
                  id="contact-subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                >
                  <option value="">请选择主题</option>
                  <option value="商务合作">商务合作</option>
                  <option value="内容授权">内容授权</option>
                  <option value="广告投放">广告投放</option>
                  <option value="意见反馈">意见反馈</option>
                  <option value="其他问题">其他问题</option>
                </select>
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-2">留言内容</label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                  placeholder="请输入您的留言（最多500字）"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {formStatus === 'submitting' ? '提交中...' : '发送留言'}
              </button>

              {formStatus === 'success' && (
                <p className="text-sm text-green-600 text-center">感谢您的留言！我们会尽快回复。</p>
              )}
              {formStatus === 'error' && (
                <p className="text-sm text-red-500 text-center">{formError}</p>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于韩漫大全 - 韩漫漫画网站',
          description: '韩漫大全致力于打造中文世界最优质的韩国漫画在线阅读平台，汇聚好看韩国漫画，支持韩漫在线免费阅读',
          url: 'https://www.example.com/about',
          isPartOf: {
            '@type': 'WebSite',
            name: '韩漫漫画网站',
            url: 'https://www.example.com',
          },
          about: {
            '@type': 'Organization',
            name: '韩漫大全',
            description: '韩漫漫画网站汇聚了众多好看韩国漫画，支持韩漫在线免费阅读',
          },
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map(faq => ({
            '@type': 'Question',
            name: faq.q,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.a,
            },
          })),
        })}
      </script>
    </>
  );
}